import PropTypes from 'prop-types';
import { Modal, Table } from 'react-bootstrap';
import { hasThirdCountryDutyRateGreaterThanZero } from '../../../Newload/QAChecklistTab/utils';

const TRADER_TARIFF_COMMODITY_BASE = 'https://www.trade-tariff.service.gov.uk/commodities';

const CheckLabel = () => <span className="text-danger text-decoration-underline fw-semibold">CHECK</span>;

const isPreferentialNo = (row) => !row || !['YES', '1', 1, true].includes(row.preferential);

const formatDutyRate = (duty, row) => {
  if (!isPreferentialNo(row)) {
    const rate = duty?.dutyExpressionRate;
    return rate != null && rate !== '' && rate !== '0.00 %' ? String(rate) : '-';
  }
  if (!duty || !hasThirdCountryDutyRateGreaterThanZero(duty)) return '-';
  const raw = duty.dutyExpression ?? duty.DutyExpression;
  if (raw != null && raw !== '') return String(raw);
  const parts = [duty.thirdCountryAV, duty.thirdCountrySpecific].filter(Boolean);
  return parts.length ? parts.join(' / ') : '-';
};

const commodityHref = (code) => {
  if (!code) return TRADER_TARIFF_COMMODITY_BASE;
  const normalized = String(code).replaceAll(/\s/g, '');
  return `${TRADER_TARIFF_COMMODITY_BASE}/${encodeURIComponent(normalized)}`;
};

function TariffLinkCell({ code }) {
  if (!code) {
    return <>-</>;
  }
  return (
    <a href={commodityHref(code)} target="_blank" rel="noopener noreferrer">
      View on Trade Tariff
    </a>
  );
}

TariffLinkCell.propTypes = {
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function DutyCheckTableBody({ rows }) {
  return (
    <Table responsive bordered hover size="sm" className="mb-0 align-middle">
      <thead className="table-light">
        <tr>
          <th>Line number</th>
          <th>Commodity code</th>
          <th>Origin</th>
          <th>Duty rate</th>
          <th>Trade remedies</th>
          <th>Excise</th>
          <th>Customs Tech</th>
          <th>Tariff</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => {
          const code = row.commcode ?? row.commoditycode ?? row.duty?.commodityCode;
          const duty = row.duty;
          const lineNo = duty?.itemId ?? row.itemId ?? idx + 1;
          return (
            <tr key={row.itemId ?? `${code}-${row.origin}-${idx}`}>
              <td>{lineNo}</td>
              <td>{code ?? '-'}</td>
              <td>{row.origin ?? duty?.origin ?? '-'}</td>
              <td>{formatDutyRate(duty, row)}</td>
              <td>
                {duty?.additionalDuties != null && duty?.additionalDuties !== '' && duty?.additionalDuties !== false ? (
                  <CheckLabel />
                ) : (
                  '-'
                )}
              </td>
              <td>{duty?.excise != null && duty?.excise !== '' && duty?.excise !== false ? <CheckLabel /> : '-'}</td>
              <td>{duty?.customsTech === true ? <CheckLabel /> : '-'}</td>
              <td>
                <TariffLinkCell code={code} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

DutyCheckTableBody.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function renderDutyCheckBody(loadFailed, noDutyDetected, rows) {
  if (loadFailed) {
    return <p className="mb-0 text-danger">Unable to load duty information. Please try again.</p>;
  }
  if (noDutyDetected) {
    return <p className="mb-0">No Potential duty detected</p>;
  }
  return <DutyCheckTableBody rows={rows} />;
}

/**
 * @param {boolean} props.show
 * @param {function} props.onHide
 * @param {boolean} props.loadFailed — could not load summary or duty rates
 * @param {boolean} props.noDutyDetected — load succeeded but no lines need duty attention
 * @param {Array<object>} props.rows — merged EPO items + `.duty` from commodityDutyRates
 */
export default function EpoDutyCheckModal({ show, onHide, loadFailed, noDutyDetected, rows = [] }) {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Duty check</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderDutyCheckBody(loadFailed, noDutyDetected, rows)}</Modal.Body>
      <Modal.Footer>
        <button type="button" className="generic-btn bg-grey" onClick={onHide} style={{ borderRadius: '10px' }}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}

EpoDutyCheckModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  loadFailed: PropTypes.bool,
  noDutyDetected: PropTypes.bool,
  rows: PropTypes.arrayOf(PropTypes.object),
};
